import type { Comparable } from '../../types/insights'

/**
 * Rekenregels voor Inzicht die overal terugkomen. Puur.
 *
 * Een kleine salon heeft kleine aantallen: "100% kwam terug" bij 2 klanten zegt niets.
 * Onder `MIN_N` geen percentage, maar het aantal ("2 van 2").
 */
export const MIN_N = 10

/** Terugkeervensters voor cohorten, in maanden. */
export const COHORT_MONTHS = [1, 3, 6] as const

/** Een cohortcel met zo weinig klanten kleurt grijs: te klein om iets over te zeggen. */
export const COHORT_MIN_SIZE = 5

/** Deel van het geheel in hele procenten, of `null` als het geheel te klein is (of onbekend). */
export function share(part: number | null, whole: number | null, min = MIN_N): number | null {
  if (part === null || whole === null || whole < min || whole <= 0) return null
  return Math.round((part / whole) * 100)
}

export type Change =
  | { kind: 'unknown' }
  /** Te weinig om in procenten te zeggen: het verschil in aantallen. */
  | { kind: 'absolute'; diff: number }
  | { kind: 'percent'; pct: number }

/**
 * Het verschil met de vorige periode. Onbekend als een van beide ontbreekt of de vorige 0 was;
 * in aantallen als beide onder `MIN_N` liggen (bij bedragen: `min = 0`, altijd procenten).
 */
export function changeOf({ current, previous }: Comparable, min = MIN_N): Change {
  if (current === null || previous === null) return { kind: 'unknown' }
  if (Math.max(current, previous) < min) return { kind: 'absolute', diff: current - previous }
  if (previous === 0) return { kind: 'unknown' }
  return { kind: 'percent', pct: Math.round(((current - previous) / Math.abs(previous)) * 100) }
}

/** Mediaan; `null` voor een lege lijst. Liever dan het gemiddelde: één uitschieter vertekent minder. */
export function median(values: readonly number[]): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2)
}

export const sum = (values: readonly number[]) => values.reduce((s, v) => s + v, 0)

/** Bezetting in hele procenten; `null` als er geen open tijd was. */
export const occupancy = (bookedMinutes: number, openMinutes: number): number | null =>
  openMinutes > 0 ? Math.round((Math.min(bookedMinutes, openMinutes) / openMinutes) * 100) : null
